import { FC } from 'react';
import { Box, Button, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material';
import { useProcessReportModalContext } from '../ProcessReportModalContext';

const ProcessReportOutputPanel: FC = () => {
  const {
    inputsSummary,
    processButtonLabel,
    canProcess,
    runExtraction,
    isPipelineBusy,
    isStartingExtraction,
    isExtractionProcessing,
    isDrafting,
    isExtractionStatusError,
    isExtractionFailed,
    pipelineStatusColor,
    pipelineStatusMessage,
    latestExtractionRun,
    dashboardOpinionUrl,
    draftEmail,
    setDraftEmail,
    draftDashboardUrl,
    handleCopyEmail,
  } = useProcessReportModalContext();

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {inputsSummary}
      </Typography>
      <Button
        variant="contained"
        onClick={() => runExtraction()}
        disabled={!canProcess}
        id="draft-final-report-email-button"
      >
        {processButtonLabel}
      </Button>
      {(isPipelineBusy || isExtractionStatusError || isExtractionFailed) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: pipelineStatusColor,
          }}
        >
          {(isExtractionProcessing || isDrafting || isStartingExtraction) && (
            <CircularProgress size={16} />
          )}
          <Typography variant="caption">{pipelineStatusMessage}</Typography>
        </Box>
      )}
      {latestExtractionRun?.run_id && !isPipelineBusy && (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'action.hover',
          }}
          data-testid="latest-extraction-run-banner"
        >
          <Typography variant="body2" color="text.secondary">
            Last extraction
            {latestExtractionRun.created_at
              ? ` (${new Date(latestExtractionRun.created_at).toLocaleString()})`
              : ''}
            : {latestExtractionRun.status ?? 'unknown'}
          </Typography>
          {dashboardOpinionUrl ? (
            <Button
              component="a"
              href={dashboardOpinionUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: 'none' }}
              id="view-dashboard-opinion-link"
              data-testid="view-dashboard-opinion-link"
            >
              View on dashboard
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Dashboard link will appear after extraction completes and data is imported.
            </Typography>
          )}
        </Box>
      )}
      <TextField
        label="Email"
        value={draftEmail}
        onChange={event => setDraftEmail(event.target.value)}
        placeholder="Email will appear here after processing completes…"
        multiline
        minRows={12}
        fullWidth
        id="final-report-email-content"
      />
      {draftDashboardUrl && (
        <Button
          component="a"
          href={draftDashboardUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, textTransform: 'none' }}
          id="draft-dashboard-opinion-link"
          data-testid="draft-dashboard-opinion-link"
        >
          View on dashboard
        </Button>
      )}
      <Divider />
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={handleCopyEmail}
          disabled={!draftEmail}
          id="copy-final-report-email-button"
        >
          Copy Email
        </Button>
        <Button variant="contained" disabled id="send-final-report-email-button">
          Send Email
        </Button>
      </Stack>
    </Stack>
  );
};

export default ProcessReportOutputPanel;
